import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

// React Router only ships `useBlocker` for data routers (createBrowserRouter).
// This app runs on <BrowserRouter>, so the guard is assembled by hand:
//   - in-app navigation (Link, navigate()) is caught by wrapping the router's
//     history object, which every navigation goes through
//   - the browser Back button is caught with a duplicate history entry (the
//     "sentinel") that absorbs the first press, since a pop cannot be undone
//     once React Router has reacted to it
//   - refresh and tab close are caught with beforeunload
const SENTINEL_KEY = '__oakUnsavedGuard';

// An expired session or a permission error redirects through the router. That is
// not the user leaving on purpose, so it must never be held up by a dialog.
const ALWAYS_ALLOWED_PREFIXES = ['/auth', '/error'];

const pathOf = (to) => (typeof to === 'string' ? to : to?.pathname || '');

const isAlwaysAllowed = (to) =>
  ALWAYS_ALLOWED_PREFIXES.some((prefix) => pathOf(to).startsWith(prefix));

// The idle tracker sets this before signing the user out, either by reloading
// (cross-tab) or by redirecting. Neither should sit behind a confirmation — an
// unattended machine has to end up logged out.
const isLoggingOut = () => {
  try {
    return Boolean(window.localStorage?.getItem('logoutTriggered'));
  } catch {
    return false;
  }
};

/**
 * Holds up any attempt to leave the current page while `isBlocked()` returns
 * true. `onBlocked` receives a `proceed` callback that carries out the
 * navigation that was held up; call it to let the user through, or drop it to
 * keep them on the page.
 *
 * @param {Object}   options
 * @param {Function} options.isBlocked  read at the moment of navigation
 * @param {Function} options.onBlocked  called with (proceed)
 */
export const useNavigationGuard = ({ isBlocked, onBlocked }) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const isBlockedRef = useRef(isBlocked);
  const onBlockedRef = useRef(onBlocked);
  const sentinelArmedRef = useRef(false);

  isBlockedRef.current = isBlocked;
  onBlockedRef.current = onBlocked;

  useEffect(() => {
    if (!navigator) return undefined;

    const guardId = `guard-${Math.random().toString(36).slice(2)}`;

    // This wraps the router's shared history object, so a predicate that throws
    // would take every link in the app down with it. Failing open costs one
    // missed warning; failing closed strands the user on the page.
    const shouldBlock = () => {
      try {
        return Boolean(isBlockedRef.current()) && !isLoggingOut();
      } catch (error) {
        console.error('Unsaved-changes check failed, allowing navigation:', error);
        return false;
      }
    };

    // A copy of the entry we are sitting on, so the first Back press lands here
    // again instead of unmounting the page. `idx` is bumped exactly the way
    // React Router bumps it on a push, so its own bookkeeping stays consistent.
    const armSentinel = () => {
      const state = window.history.state || {};
      const idx = typeof state.idx === 'number' ? state.idx + 1 : state.idx;
      window.history.pushState({ ...state, idx, [SENTINEL_KEY]: guardId }, '');
      sentinelArmedRef.current = true;
    };

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const originalGo = navigator.go;

    // The entry we are on is the sentinel — a duplicate of this page — so a push
    // has to overwrite it. Pushing on top instead would strand the duplicate and
    // make Back land on this page twice.
    const runPush = (...args) => {
      if (sentinelArmedRef.current) {
        sentinelArmedRef.current = false;
        originalReplace.apply(navigator, args);
        return;
      }
      originalPush.apply(navigator, args);
    };

    const runReplace = (...args) => {
      sentinelArmedRef.current = false;
      originalReplace.apply(navigator, args);
    };

    const runGo = (delta) => {
      const adjusted = sentinelArmedRef.current && delta < 0 ? delta - 1 : delta;
      sentinelArmedRef.current = false;
      originalGo.call(navigator, adjusted);
    };

    navigator.push = (...args) => {
      if (isAlwaysAllowed(args[0]) || !shouldBlock()) {
        runPush(...args);
        return;
      }
      onBlockedRef.current(() => runPush(...args));
    };

    navigator.replace = (...args) => {
      if (isAlwaysAllowed(args[0]) || !shouldBlock()) {
        runReplace(...args);
        return;
      }
      onBlockedRef.current(() => runReplace(...args));
    };

    navigator.go = (delta) => {
      if (!shouldBlock()) {
        runGo(delta);
        return;
      }
      onBlockedRef.current(() => runGo(delta));
    };

    const handlePopState = () => {
      // Landing on the sentinel is a Forward press and needs no handling.
      if (window.history.state?.[SENTINEL_KEY] === guardId) return;
      if (!sentinelArmedRef.current) return;

      sentinelArmedRef.current = false;

      if (!shouldBlock()) {
        // Nothing to protect, and the sentinel swallowed the press. Repeat it so
        // the Back button still does what the user expects.
        originalGo.call(navigator, -1);
        return;
      }

      // Put the sentinel back so the page stays put while the user decides.
      // Leaving then means stepping over both the sentinel and this entry.
      armSentinel();
      onBlockedRef.current(() => {
        sentinelArmedRef.current = false;
        originalGo.call(navigator, -2);
      });
    };

    const handleBeforeUnload = (event) => {
      if (!shouldBlock()) return;
      event.preventDefault();
      // Older browsers need returnValue set; the text itself is always ignored.
      event.returnValue = '';
    };

    armSentinel();
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      navigator.go = originalGo;
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [navigator]);
};
